import {createAsyncThunk} from '@reduxjs/toolkit'
import {RouterState} from 'redux-first-history'

import {isApiError} from '@shared/helpers/api'
import {eq} from '@shared/helpers/lang'

import {openPopup} from '@web/actions/application'
import {DELIVERY_MANAGE_SCENE, DELIVERY_MANAGER_COMPLETED, DELIVERY_MEMO, PopupSize} from '@web/core/constants'
import {decodeHtmlEntities as decode} from '@web/helpers/htmlEntities'
import {isMobileDisplay} from '@web/helpers/userAgent'
import {commonSelector, CommonState, orderEntryInfoSelector, pageSetInfoSelector} from '@web/slices/common/slice'
import {BaseAddressInfo} from '@web/slices/deliveryInfo/slice'
import {
    AddressBy,
    closeDeliveryManagerWithFormConfirmTypes,
    DEFAULT_DELIVERY_MANAGER_TYPE,
    DELIVERY_MANAGER_TYPE,
    deliveryManagerSelector,
    DeliveryManagerState,
    DeliveryManagerType,
    getInitialAddressSearchQuery,
    getInitialOrderInfo,
    InitDeliveryManagerPayload,
    mergeStorageDataToOriginalPayload,
    OrderInfo,
    saveDeliveryManagerSessionStorage,
    shouldFetchOrderInfoTypes,
} from '@web/slices/deliveryManager/slice'
import {locationSelector, push, replace} from '@web/slices/router'

import apis from './apis/orderManager'
import {getDeliveryAddressBadge, getDeliveryManagerUrl, getInitialSceneWithType} from './utils'

/**
 * 배송지변경창 오픈액션.
 * order-web에서 액션으로 배송지변경창 오픈 시, openDeliveryManager -> initDeliveryManagerStore -> initDeliveryManagerScene 순서로 동작.
 * 외부에서 url로 직접 접근 시, initDeliveryManagerStore 부터 동작
 */
export const openDeliveryManager = createAsyncThunk<
    {type: DeliveryManagerType},
    {
        type: DeliveryManagerType
    },
    {
        state: {router: RouterState; common: CommonState}
    }
>('deliveryManager/open', ({type}, {getState, dispatch}) => {
    const {state: locationState} = locationSelector(getState())
    const {seqID} = commonSelector(getState())
    const {isShoppingSubscription} = pageSetInfoSelector(getState())

    const url = getDeliveryManagerUrl(type, isShoppingSubscription, seqID)

    if (isMobileDisplay) {
        dispatch(
            push(window.location.href, {
                state: {
                    ...locationState,
                    deliveryManagerScene: DELIVERY_MANAGE_SCENE.INITIAL,
                },
            }),
        )
    } else {
        dispatch(
            openPopup({
                key: DELIVERY_MANAGER_COMPLETED,
                url,
                title: '배송지 관리',
                size: PopupSize.common,
                refreshPopupOnUrlDiff: true,
            }),
        )
    }

    return {type}
})

/**
 * 배송지변경창 scene 초기화 액션.
 * initDeliveryManagerStore 실행 후 실행된다.
 * 스토어에 저장된 오픈타입에 따라 scene을 결정한다.
 * 최초 1번만 실행 후 locationState에 scene 저장, 이후에 locationState에 scene이 있다면 해당 액션이 실행되지 않는다.
 * */
export const initDeliveryManagerScene = createAsyncThunk<
    void,
    void,
    {state: {router: RouterState; deliveryManager: DeliveryManagerState}}
>('deliveryManager/initScene', (_, {dispatch, getState}) => {
    const {state: locationState} = locationSelector(getState())
    const {
        options: {type},
        status: {historyCountFromBase},
    } = deliveryManagerSelector(getState())

    const url = window.location.href
    // todo: 초기화 후 internal query 정리
    // const {cleanUrl} = extractQueryFromUrl(url, ['internal'])

    const shouldInitHistoryFromBase =
        !!type && historyCountFromBase === 0 && closeDeliveryManagerWithFormConfirmTypes.includes(type)

    dispatch(
        replace(url, {
            state: {
                ...locationState,
                ...(shouldInitHistoryFromBase && {historyCountFromBase: 1}),
                deliveryManagerScene: getInitialSceneWithType(type || DEFAULT_DELIVERY_MANAGER_TYPE),
            },
        }),
    )
})

/**
 * deliveryManager store 초기화 액션.
 * 페이로드에 들어올 데이터의 출처는 다음과 같다.
 * - pc : postMessage로 데이터를 받아 이 액션으로 전달,
 * - mobile : 각 슬라이스에서 데이터를 긁어와 이 액션으로 전달,
 *
 * 주문후 배송지변경창인 케이스에는 orderManager에서 주문데이터를 페치해 온 데이터로 스토어를 초기화한다.
 *  (액션 페이로드로 받은 데이터는 무시한다)
 * */
export const initDeliveryManagerStore = createAsyncThunk<
    InitDeliveryManagerPayload,
    {
        type: DeliveryManagerType
        fromParent: {
            deliveryAddress: Maybe<BaseAddressInfo>
            orderInfo: Maybe<OrderInfo>
        }
        /** [TODO] feat/14629: path에서 deliveryManager store에 저장할 방법 찾기. */
        addressBy: AddressBy
    },
    {
        state: {router: RouterState; common: CommonState; deliveryManager: DeliveryManagerState}
        rejectValue: {code: string}
    }
>('deliveryManager/initStore', async ({type, fromParent, addressBy}, {getState, rejectWithValue}) => {
    const {seqID} = commonSelector(getState())

    if (shouldFetchOrderInfoTypes.some(eq(type))) {
        try {
            const data = await apis.getDelivery({orderNo: seqID, addressBy})

            const parseMemoType = (memoValue: Maybe<string>) => {
                const memoBook = data.deliveryMemos
                const foundMemo = memoBook.find(({memo}) => memo === memoValue)

                if (!memoValue) {
                    return DELIVERY_MEMO.NOTHING
                } else if (foundMemo && foundMemo.template) {
                    return DELIVERY_MEMO.COMMON
                } else {
                    return DELIVERY_MEMO.WRITTEN
                }
            }

            const {isGifting, isBranch} = orderEntryInfoSelector(getState())
            const isParticularMemoUsable = !isGifting && !isBranch && data.deliveryAddress.deliveryMemos.length > 1

            const needChangeTypeToContactChange = data.viewAdditionalInfo.hasAnyArrivalGuaranteed
            const fixedType = needChangeTypeToContactChange ? DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY : type

            const shouldInitDeliveryAddress = fixedType === DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY

            const initPayloadFromApi: InitDeliveryManagerPayload = {
                type: fixedType,
                address: null,
                deliveryAddress: shouldInitDeliveryAddress
                    ? {
                          hash: data.deliveryAddress.hash || '',
                          addressName: decode(data.deliveryAddress.addressName) || '',
                          addressNameBadge: getDeliveryAddressBadge(decode(data.deliveryAddress.addressName) || ''),
                          receiverName: decode(data.deliveryAddress.receiverName),
                          zipCode: data.deliveryAddress.zipCode || '',
                          buildingManagementNo: data.deliveryAddress.buildingManagementNo || '',
                          locationX: data.deliveryAddress.locationX || '',
                          locationY: data.deliveryAddress.locationY || '',
                          baseAddress: decode(data.deliveryAddress.baseAddress) || '',
                          detailAddress: decode(data.deliveryAddress.detailAddress),
                          isRoadNameAddress: data.deliveryAddress.isRoadNameAddress || false,
                          telNo1: data.deliveryAddress.telNo1,
                          telNo2: data.deliveryAddress.telNo2 || '',
                          isPrimaryAddress: false,
                          isRegistered: false,
                      }
                    : null,
                orderInfo: {
                    originalAddressHash: data.deliveryAddress.hash || '',
                    isOverseasDelivery: data.viewAdditionalInfo.isOverseasDelivery || false,
                    hasArrivalGuaranteeProduct: data.viewAdditionalInfo.hasAnyArrivalGuaranteed || false,
                    deliveryMemo: {
                        memoList: data.deliveryAddress.deliveryMemos.map((memo) => {
                            const productData = memo.productName
                                ? {
                                      productName: memo.productName,
                                      productOrderNo: memo.productOrderNo,
                                      optionContents: memo.optionContents || '',
                                  }
                                : undefined
                            return {
                                value: decode(memo.deliveryMemo) || '',
                                type: parseMemoType(memo.deliveryMemo),
                                product: productData,
                            }
                        }),
                        options: {
                            particularMemo: {
                                used: data.deliveryAddress.isParticularInput || false,
                                usable: isParticularMemoUsable,
                            },
                            saveAsDefaultMemo: false,
                        },
                    },
                    virtualPhoneNumber: {
                        usable: data.viewAdditionalInfo.isVirtualPhoneNumberUsable || false,
                        used: data.deliveryAddress.usingVirtualPhoneNumber,
                    },
                    ...(data.deliveryAddress.personalCustomsCode
                        ? {
                              personalCustomsCode: {
                                  maskedCode: data.deliveryAddress.personalCustomsCode?.code,
                                  hash: data.deliveryAddress.personalCustomsCode?.hash,
                                  newCode: data.deliveryAddress.personalCustomsCode?.code,
                                  errorStatus: 'IDLE',
                              },
                          }
                        : {personalCustomsCode: null}),
                },
                searchQuery: getInitialAddressSearchQuery(),
            }

            const payload = mergeStorageDataToOriginalPayload(initPayloadFromApi)

            saveDeliveryManagerSessionStorage(payload)

            return payload
        } catch (e) {
            if (isApiError(e)) {
                const orderManagerServerErrorCode = e.response?.data?.result?.code ?? ''

                if (orderManagerServerErrorCode) {
                    return rejectWithValue({
                        code: orderManagerServerErrorCode,
                    })
                } else {
                    return rejectWithValue({
                        code: 'NOT_FOUND',
                    })
                }
            }
            throw e
        }
    } else {
        // 주문서 배송지변경창 초기화 (주문전)
        const orderInfo = fromParent.orderInfo || getInitialOrderInfo()
        const deliveryAddress = fromParent.deliveryAddress
            ? {
                  ...fromParent.deliveryAddress,
                  addressNameBadge: getDeliveryAddressBadge(fromParent.deliveryAddress?.addressName || ''),
              }
            : null

        const shouldSetSearchKeyword = type === DELIVERY_MANAGER_TYPE.CORRECT_INVALID_DELIVERY_ADDRESS
        const searchKeyword = shouldSetSearchKeyword ? deliveryAddress?.baseAddress || '' : ''

        const initPayloadFromParent: InitDeliveryManagerPayload = {
            type,
            address: null,
            deliveryAddress,
            orderInfo,
            searchQuery: {
                ...getInitialAddressSearchQuery(),
                keyword: searchKeyword,
            },
        }
        const payload = mergeStorageDataToOriginalPayload(initPayloadFromParent)

        saveDeliveryManagerSessionStorage(payload)

        return payload
    }
})
